import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ArrowRightAlt from '@material-ui/icons/ArrowRightAlt';
import { isMobileOnly, isTablet } from 'react-device-detect';
import { StatusCodes } from 'http-status-codes';
import orderBy from 'lodash.orderby';

import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import ControlledFeatureService from 'services/ControlledFeatureService';
import EpayService from 'services/EpayService/EpayService';
import { translate } from 'services/i18nService';
import Button from 'components/Button/Button';
import GoldMembership from 'components/GoldMembership/GoldMembership';
import LoadingLogo from 'components/LoadingLogo/LoadingLogo';
import Modal from 'components/Modal/Modal';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import { EPAY_PRODUCT_TYPES } from 'enum/payments';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import { generateID } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';
import {
  actionCreators as paymentActionsObject,
  selector as paymentSelector,
} from 'features/Payment/State';
import {
  actionCreators as directoryListActions,
  selector as directorySelector,
} from 'pages/Directory/State';

import goldCam4Image from '../../../images/goldcam4.png';

import PaymentMethodList from './PaymentMethodList/PaymentMethodList';
import styles from './UpgradeToGoldModal.css';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);
const DEVICES = {
  mobile: 'mobile',
  tablet: 'tablet',
  desktop: 'desktop',
};

const initialState = {
  loading: false,
  step: 0,
  isSubmiting: {
    type: null,
    loading: false,
  },
};

@connect(paymentSelector, (dispatch) => {
  return { paymentActions: bindActionCreators(paymentActionsObject, dispatch) };
})
@connect(directorySelector, (dispatch) => {
  return { directoryActions: bindActionCreators(directoryListActions, dispatch) };
})
class UpgradeToGoldModal extends Component {
  static propTypes = {
    directoryActions: PropTypes.object.isRequired,
    isUpgradeToGoldModalVisible: PropTypes.bool.isRequired,
    paymentActions: PropTypes.object.isRequired,
    user: PropTypes.object.isRequired,
  };

  constructor(...args) {
    super(...args);

    this.IDs = {
      prefix: '99dAsEsK',
      element: 'upgradeToGoldModal',
      membershipBonus: 'membershipBonus',
      limit: 'limit',
      learnAboutGold: 'learnAboutGold',
      buttonSelect: 'buttonSelect',
    };
    this.makeId = generateID(this.IDs.prefix, this.IDs.element);
  }

  state = initialState;

  componentDidMount() {
    const { user, isUpgradeToGoldModalVisible } = this.props;
    if (isUpgradeToGoldModalVisible && user.username) {
      this.getProducts();
    }
  }

  componentDidUpdate(prevProps) {
    const { user, isUpgradeToGoldModalVisible } = this.props;
    if (
      prevProps.isUpgradeToGoldModalVisible !== isUpgradeToGoldModalVisible &&
      isUpgradeToGoldModalVisible &&
      user.username
    ) {
      if (EpayService.selectedMembership) {
        this.handleMembershipClick(EpayService.selectedMembership);
      } else {
        this.getProducts();
      }
    }
  }

  getProducts = () => {
    const { user: { username, accessHash } } = this.props;
    this.setState(
      { loading: true },
      () => {
        EpayService.getProducts(username, accessHash, EPAY_PRODUCT_TYPES.MEMBERSHIP)
          .then((res) => {
            if (res.status === StatusCodes.OK) {
              EpayService.setMemberships(res.data.products);
            }
            this.setState({ loading: false });
          })
          .catch((err) => {
            parseCatch(err);
            this.setState({ loading: false });
          });
      }
    );
  };

  handleMembershipClick = (membership) => {
    if (membership.item.categories.length < 2) {
      const method = membership.item.categories[0].methods[0];
      const prodId = membership.id;
      this.onPaymentMethodClick(method, prodId);
    } else {
      EpayService.setSelectedMembership(membership);
      this.setState({ step: 1 });
    }
  };

  membershipHeader = () => {
    const membership = EpayService.memberships[0] ? EpayService.memberships[0] : null;
    const details = membership && membership.item && membership.item.details;
    const bonus = details && details.bonus ? details.bonus : 0;
    const { user } = this.props;

    let text = undefined;
    if (user?.hasMembershipBonus) {
      text = `${translate('epay.epay.gold.free.tokens.taken')}`;
    } else {
      text = `${translate('epay.epay.gold.free.tokens.once.you.upgrade', { 0: bonus })}*`;
    }

    return (
      <header className={styles.headerWithMembership}>
        <img alt="cam4 logo" src={goldCam4Image} width="96" />
        <div className={styles.membershipBonus} id={this.makeId(this.IDs.membershipBonus)}>
          {text}
        </div>
      </header>
    );
  };

  membershipFooter = () => (
    <footer className={!this.props.user.hasMembershipBonus ? styles.footerWithMembershipBonus : styles.footer}>
      {!this.props.user.hasMembershipBonus && (
        <span className={styles.limit} id={this.makeId(this.IDs.limit)}>
          *{translate('gold.v2.limit.per.customer')}
        </span>
      )}
      <a href={routeDefinitions.gold} id={this.makeId(this.IDs.learnAboutGold)}>
        {translate('gold.upgrade.footer.description')}
      </a>
    </footer>
  );

  singleMembershipFooter = () => (
    <footer className={styles.footer}>
      <button className={styles.actionLink} onClick={this.onCloseModal}>
        {translate('strings.v2.later')}
      </button>
    </footer>
  );

  hasSingleMembership = () =>
    ControlledFeatureService.setUser(this.props.user).has(allUserGroups.GOLD_OVERLAY_ONE_PACKAGE);

  isNotGoldUser = () => this.props.user.accessLevel < ACCESS_LEVEL_NUM.GOLD;

  onPaymentMethodClick = (method, prodId) => {
    const { type, processorId, issuerValue } = method;
    const { user: { username, accessHash, email, user } } = this.props;
    const id = EpayService.selectedMembership ? EpayService.selectedMembership.id : prodId;
    const activeCampaigns = user.get('user').get('activeCampaigns');
    const activeCampaign = activeCampaigns && activeCampaigns.size && activeCampaigns.get(0);
    const campaignDetails = activeCampaign && activeCampaign.get('campaignDetails');
    const showXSell = campaignDetails && campaignDetails.get('showXSell');
    let currentDevice = '';
    if (isMobileOnly) {
      currentDevice = DEVICES.mobile;
    } else if (isTablet) {
      currentDevice = DEVICES.tablet;
    } else {
      currentDevice = DEVICES.desktop;
    }

    const options = {
      originalDomain: BASE_URL || location.origin,
      siteSubcat: 'cam4.com',
      returnPath: routeDefinitions.checkout,
      accessHash,
      device: currentDevice,
      email,
      paymentType: type,
      issuer: issuerValue,
    };

    if (showXSell) {
      options.showXSell = showXSell;
    }

    this.setState({
      isSubmiting: {
        type,
        loading: true,
      },
    });
    EpayService.openPaymentProcessorWindow(processorId);

    EpayService.getPaymentProcessorData(processorId, id, username, options)
      .then((res) => {
        this.setState(
          {
            isSubmiting: {
              type: null,
              loading: false,
            },
          },
          () => this.closeModal()
        );
        EpayService.prepareForm(res.data.nextStage);

        try {
          EpayService.submitForm();
        } catch (error) {
          EpayService.submitForm();
        }
      })
      .catch((err) => {
        this.setState(
          {
            isSubmiting: {
              type: null,
              loading: false,
            },
          },
          () => {
            this.closeModal();
            EpayService.closePaymentProcessorWindow();
          }
        );
        parseCatch(err);
      });
  };

  onUpgradeNowClick = () => {
    const membership = EpayService.memberships[0];
    this.handleMembershipClick(membership);
  };

  onMailClick = (membership) => {
    this.handleMembershipClick(membership);
  };

  onCloseModal = () => {
    const {
      paymentActions: { hideUpgradeToGoldModal },
      directoryActions,
    } = this.props;

    this.setState(
      { loading: false },
      () => {
        hideUpgradeToGoldModal();
        this.setState({ step: 0 }, () => EpayService.setSelectedMembership(null));
      }
    );
    directoryActions.setIsAnyModalShown(false);
  };

  renderAction = (membership) => (
    <Button
      className={styles.buttonSelect}
      id={`${this.makeId(this.IDs.buttonSelect)}_${membership.months}M`}
      onClick={this.onMailClick}
    >
      {translate('mail.massoptions.selectLabel')}
      <ArrowRightAlt />
    </Button>
  );

  renderGoldMembership = () => {
    const { loading } = this.state;

    const memberships = orderBy(EpayService.memberships, ['months'], ['desc']);
    const specialCurrencySymbols = ['$', '£', '€', 'CHF'];

    let singleMembershipPrice = '';
    const membership = EpayService.memberships?.[0];
    if (membership) {
      const { currency, amount } = membership;
      if (specialCurrencySymbols.indexOf(currency) > -1) {
        singleMembershipPrice = `${currency}${amount}`;
      } else {
        singleMembershipPrice = `${amount}${currency}`;
      }
    }

    return !this.hasSingleMembership() && this.isNotGoldUser() ? (
      <GoldMembership
        actions={this.renderAction}
        footer={this.membershipFooter()}
        header={this.membershipHeader()}
        loading={loading}
        memberships={memberships}
      />
    ) : (
      <GoldMembership footer={this.singleMembershipFooter()} header={this.membershipHeader()}>
        <div className={styles.singleMembership}>
          <h4>{translate('gold.gold.upgrade.single.membership.title')}</h4>
          <p>
            {translate(
              'epay.epay.gold.upgrade.single.membership.desc.new',
              { 0: singleMembershipPrice }
            )}
          </p>
          <ul className={styles.singleMembershipList}>
            <li>{translate('epay.epay.gold.upgrade.single.membership.feature.1.new')}</li>
            <li>{translate('gold.gold.upgrade.single.membership.feature.2')}</li>
            <li>{translate('gold.gold.upgrade.single.membership.feature.3')}</li>
          </ul>
          <Button
            colour="green"
            id={this.makeId(`${this.IDs.buttonSelect}_1M`)}
            onClick={this.onUpgradeNowClick}
          >
            {translate('epay.epay.upgrade.now')}
          </Button>
          <p className={styles.singleMembershipHelperText}>
            *{translate('epay.firsttime.purchase.offer.limit')}
          </p>
        </div>
      </GoldMembership>
    );
  };

  renderPaymentMethods = () => {
    const { loading, isSubmiting, step } = this.state;
    if (loading) {
      return <LoadingLogo />;
    }

    if (step === 0) {
      return this.renderGoldMembership();
    }

    return (
      <PaymentMethodList
        isSubmiting={isSubmiting}
        product={EpayService.selectedMembership}
        onPayClick={this.onPaymentMethodClick}
      />
    );
  };

  render() {
    const { isUpgradeToGoldModalVisible } = this.props;
    const { loading, step } = this.state;

    const modalClassName = loading ? `${styles.buyGold} ${styles.loadingLogo}` : styles.buyGold;

    return (
      <Modal
        background={loading ? 'transparent' : 'white'}
        className={modalClassName}
        close={this.closeModal}
        scroll={!loading}
        show={isUpgradeToGoldModalVisible}
        showClose={!loading}
        size={step === 0 ? 'medium' : 'large'}
        type="full"
      >
        {this.renderPaymentMethods()}
      </Modal>
    );
  }
}

export default UpgradeToGoldModal;
